import { useEffect, useState } from "react";
const FEATURES_QUERY_PARAM = "__features";
const FEATURES_STORAGE_KEY = "hub-ui:features";
const featureListeners = new Set();
let globalEnabledFeatures = new Set();
let didInitFromBrowser = false;
const isBrowser = () => typeof window !== "undefined";
const normalizeFeatureName = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(trimmed))
        return null;
    return trimmed;
};
const readStoredFeatures = () => {
    if (!isBrowser())
        return new Set();
    try {
        const raw = window.localStorage.getItem(FEATURES_STORAGE_KEY);
        if (!raw)
            return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return new Set();
        const normalized = parsed
            .filter((v) => typeof v === "string")
            .map((v) => normalizeFeatureName(v))
            .filter((v) => v !== null);
        return new Set(normalized);
    }
    catch {
        return new Set();
    }
};
const writeStoredFeatures = (features) => {
    if (!isBrowser())
        return;
    const sorted = [...features].sort();
    window.localStorage.setItem(FEATURES_STORAGE_KEY, JSON.stringify(sorted));
};
const readQueryDelta = () => {
    if (!isBrowser())
        return { kind: "absent" };
    const params = new URLSearchParams(window.location.search);
    if (!params.has(FEATURES_QUERY_PARAM))
        return { kind: "absent" };
    const raw = params.get(FEATURES_QUERY_PARAM) ?? "";
    if (raw.trim() === "")
        return { kind: "clear_all" };
    const enable = [];
    const disable = [];
    for (const token of raw.split(",")) {
        const trimmed = token.trim();
        if (!trimmed)
            continue;
        if (trimmed.startsWith("-")) {
            const name = normalizeFeatureName(trimmed.slice(1));
            if (name)
                disable.push(name);
            continue;
        }
        const name = normalizeFeatureName(trimmed);
        if (name)
            enable.push(name);
    }
    return { kind: "delta", enable, disable };
};
const computeEnabledFeaturesFromBrowser = () => {
    const base = readStoredFeatures();
    const delta = readQueryDelta();
    if (delta.kind === "absent")
        return base;
    if (delta.kind === "clear_all")
        return new Set();
    for (const name of delta.disable)
        base.delete(name);
    for (const name of delta.enable)
        base.add(name);
    return base;
};
const notifyFeaturesChanged = () => {
    const snapshot = new Set(globalEnabledFeatures);
    for (const listener of featureListeners)
        listener(snapshot);
};
const setEnabledFeatures = (next) => {
    globalEnabledFeatures = next;
    writeStoredFeatures(globalEnabledFeatures);
    notifyFeaturesChanged();
};
export const initFeaturesFromBrowser = () => {
    if (!isBrowser())
        return;
    if (didInitFromBrowser)
        return;
    didInitFromBrowser = true;
    globalEnabledFeatures = computeEnabledFeaturesFromBrowser();
    writeStoredFeatures(globalEnabledFeatures);
    notifyFeaturesChanged();
};
export const setFeatureEnabled = (feature, enabled) => {
    if (!isBrowser())
        return;
    initFeaturesFromBrowser();
    const normalized = normalizeFeatureName(feature);
    if (!normalized)
        return;
    const next = new Set(globalEnabledFeatures);
    if (enabled)
        next.add(normalized);
    else
        next.delete(normalized);
    setEnabledFeatures(next);
};
export const clearAllFeatures = () => {
    if (!isBrowser())
        return;
    initFeaturesFromBrowser();
    setEnabledFeatures(new Set());
};
export const useEnabledFeatures = () => {
    const [enabled, setEnabled] = useState(() => new Set());
    useEffect(() => {
        initFeaturesFromBrowser();
        const listener = (next) => setEnabled(next);
        featureListeners.add(listener);
        listener(new Set(globalEnabledFeatures));
        return () => {
            featureListeners.delete(listener);
        };
    }, []);
    return enabled;
};
export const useFeaturesEnabled = (features) => {
    const enabled = useEnabledFeatures();
    return features.map((feature) => {
        const normalized = normalizeFeatureName(feature);
        if (!normalized)
            return false;
        return enabled.has(normalized);
    });
};
export const isPreviewMode = (enabled) => enabled.size > 0;
export const useIsPreviewMode = () => {
    const enabled = useEnabledFeatures();
    return isPreviewMode(enabled);
};
export const getFeaturesQueryValue = (enabled) => [...enabled].sort().join(",");
